import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { useCallback, useId, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { api } from "../api/client";
import type { Alert } from "../api/types";
import { formatDateTime } from "../lib/format";
import { useDismiss } from "../lib/hooks";

const ALERTS = ["alerts"] as const;

/** The bell: alerts about your tickets. Live updates refresh it; the slow poll is a safety net. */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const wrapper = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, wrapper, button);

  const alerts = useQuery({
    queryKey: ALERTS,
    queryFn: ({ signal }) => api<{ items: Alert[]; unread: number }>("/api/notifications", { signal }),
    refetchInterval: 5 * 60_000,
  });
  const markRead = useMutation({
    mutationFn: (body: { ids?: string[]; all?: boolean }) =>
      api<undefined>("/api/notifications/read", { method: "POST", body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ALERTS }),
  });
  const unread = alerts.data?.unread ?? 0;
  const label = unread ? `Notifications, ${unread} unread` : "Notifications";

  return (
    <div className="profile" ref={wrapper}>
      <button
        ref={button}
        type="button"
        className="icon-btn"
        aria-label={label}
        data-tooltip="Notifications"
        data-tooltip-align="end"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
      >
        <Bell size={20} aria-hidden="true" />
        {unread ? (
          <span className="bell-count" data-count={unread > 9 ? "9+" : String(unread)} aria-hidden="true" />
        ) : null}
      </button>
      {open ? (
        <div id={panelId} className="popover alert-panel">
          <div className="row-between">
            <span className="cell-strong">Notifications</span>
            {unread ? (
              <button type="button" className="link-btn" onClick={() => markRead.mutate({ all: true })}>
                Mark all read
              </button>
            ) : null}
          </div>
          {alerts.data?.items.length ? (
            <ul className="alert-list">
              {alerts.data.items.map((alert) => (
                <li key={alert.id}>
                  <button
                    type="button"
                    className={alert.read ? "alert-item" : "alert-item alert-unread"}
                    onClick={() => {
                      if (!alert.read) markRead.mutate({ ids: [alert.id] });
                      setOpen(false);
                      if (alert.ticket) navigate(`/tickets/${alert.ticket}`);
                      else if (alert.kind === "email_held") navigate("/admin/email");
                    }}
                  >
                    <span className="alert-title">{alert.title}</span>
                    <time className="cell-secondary" dateTime={alert.created_at}>
                      {formatDateTime(alert.created_at)}
                    </time>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Nothing yet. Updates about your tickets appear here.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
